import { readFile } from "node:fs/promises";
import { parse as parseYaml } from "yaml";
import { ZodError } from "zod";
import { ComponentSchema, type ComponentDSL } from "./schema.js";

export class ComponentParseError extends Error {
  constructor(
    message: string,
    public readonly filePath?: string,
    public readonly cause?: unknown,
  ) {
    super(filePath ? `${filePath}: ${message}` : message);
    this.name = "ComponentParseError";
  }
}

function formatZodError(error: ZodError, filePath?: string): string {
  const prefix = filePath ? `${filePath}: ` : "";
  const details = error.issues
    .map((issue) => {
      const path = issue.path.length > 0 ? issue.path.join(".") : "(root)";
      return `${path}: ${issue.message}`;
    })
    .join("; ");
  return `${prefix}Validation failed: ${details}`;
}

function normalizeOperation(
  op: Record<string, unknown>,
): Record<string, unknown> {
  return {
    description: op.description,
    agentWorkflow: op.agentWorkflow ?? op.agent_workflow,
    agentTask: op.agentTask ?? op.agent_task,
    handler: op.handler,
    options: op.options,
    input: op.input,
    output: op.output,
    artifactSlots: op.artifactSlots ?? op.artifact_slots,
    riskLevel: op.riskLevel ?? op.risk_level,
    idempotent: op.idempotent,
    requiresConfirmation: op.requiresConfirmation ?? op.requires_confirmation,
    execution: op.execution,
    memoryRef: op.memoryRef ?? op.memory_ref,
  };
}

function normalizeComponent(
  raw: Record<string, unknown>,
): Record<string, unknown> {
  const operations = raw.operations;
  const normalizedOps: Record<string, unknown> = {};

  if (operations && typeof operations === "object" && !Array.isArray(operations)) {
    for (const [name, op] of Object.entries(operations)) {
      if (op && typeof op === "object" && !Array.isArray(op)) {
        normalizedOps[name] = normalizeOperation(op as Record<string, unknown>);
      } else {
        normalizedOps[name] = op;
      }
    }
  }

  return {
    schema: raw.schema,
    info: raw.info,
    implementation: raw.implementation,
    embedded_dsl_dir: raw.embedded_dsl_dir,
    projections: raw.projections,
    governance: raw.governance,
    bindings: raw.bindings,
    operations:
      Object.keys(normalizedOps).length > 0 ? normalizedOps : raw.operations,
  };
}

function validateComponent(raw: unknown, filePath?: string): ComponentDSL {
  const normalized =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? normalizeComponent(raw as Record<string, unknown>)
      : raw;

  const result = ComponentSchema.safeParse(normalized);
  if (!result.success) {
    throw new ComponentParseError(
      formatZodError(result.error, filePath),
      filePath,
      result.error,
    );
  }
  return result.data;
}

/**
 * Parse and validate a component YAML string.
 * When `filePath` is provided, `$ref` pointers resolve relative to its directory.
 */
export function parseComponentString(
  content: string,
  filePath?: string,
): ComponentDSL {
  let raw: unknown;
  try {
    raw = parseYaml(content);
  } catch (err) {
    throw new ComponentParseError(
      `Invalid YAML: ${(err as Error).message}`,
      filePath,
      err,
    );
  }

  return validateComponent(raw, filePath);
}

/**
 * Read a component YAML file, resolve `$ref` pointers, and validate.
 */
export async function parseComponentFile(
  filePath: string,
): Promise<ComponentDSL> {
  let content: string;
  try {
    content = await readFile(filePath, "utf-8");
  } catch (err) {
    throw new ComponentParseError(
      `Cannot read file: ${(err as Error).message}`,
      filePath,
      err,
    );
  }
  return parseComponentString(content, filePath);
}
